import React, { useState, useRef, useEffect } from 'react';
import {
    EvaluationArea, EvaluationParameter, EvaluationIndicatorType,
    AutomaticIndicatorCode, SubcontractorEvaluationConfig, Profile,
    EvaluationRange, EvaluationRangeOperator, VisualDistinctionType,
    VisualDistinctionLevel
} from '../../../types';
import { KlFullModal, KlDialog } from '../modal';
import {
    DEFAULT_EVALUATION_RANGES, DISTINCTION_LEVELS, RANGE_OPERATOR_OPTIONS,
    EvaluationRangeBadge, VISUAL_SETS, AUTOMATIC_INDICATOR_OPTIONS
} from '../../payments/EvaluationConfigModal';

interface MobileEvaluationConfigModalProps {
    open: boolean;
    onClose: () => void;
    config: SubcontractorEvaluationConfig;
    onSaveConfig: (updatedConfig: SubcontractorEvaluationConfig) => void;
    globalProfiles?: Profile[];
    hasEditPermission?: boolean;
}

export const MobileEvaluationConfigModal: React.FC<MobileEvaluationConfigModalProps> = ({
    open,
    onClose,
    config,
    onSaveConfig,
    globalProfiles = [],
    hasEditPermission = true
}) => {
    // Pestañas: 'evaluacion' | 'rangos'
    const [activeTab, setActiveTab] = useState<'evaluacion' | 'rangos'>('evaluacion');

    // Estado local de áreas
    const [areas, setAreas] = useState<EvaluationArea[]>(() => config.areas || []);

    // Estado local de rangos
    const [ranges, setRanges] = useState<EvaluationRange[]>(() => {
        if (config.ranges && config.ranges.length > 0) {
            return config.ranges.map(r => {
                const matchedLevel = DISTINCTION_LEVELS.find(d => d.level === r.level) || DISTINCTION_LEVELS.find(d => d.color === r.color) || DISTINCTION_LEVELS[0];
                return {
                    ...r,
                    visualType: (r.visualType === 'carita' || r.visualType === 'semaforo' || r.visualType === 'flecha') ? r.visualType : 'carita',
                    level: matchedLevel.level,
                    color: matchedLevel.color,
                    icon: matchedLevel.icons[r.visualType as VisualDistinctionType] || matchedLevel.icons.carita
                };
            });
        }
        return DEFAULT_EVALUATION_RANGES;
    });

    // Acordeón de Áreas: primera área abierta por defecto
    const [expandedAreaIds, setExpandedAreaIds] = useState<Set<string>>(() => {
        return new Set(config.areas && config.areas[0] ? [config.areas[0].id] : []);
    });

    // Menú de acción activo (⋮)
    const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Diálogo Área
    const [areaDialogOpen, setAreaDialogOpen] = useState(false);
    const [editingArea, setEditingArea] = useState<{ id?: string; name: string; weight: number }>({ name: '', weight: 25 });

    // Diálogo Parámetro
    const [paramDialogOpen, setParamDialogOpen] = useState(false);
    const [targetAreaIdForParam, setTargetAreaIdForParam] = useState<string | null>(null);
    const [editingParam, setEditingParam] = useState<{
        id?: string;
        name: string;
        weight: number;
        profileId: string;
        indicatorDescription: string;
        indicatorType: EvaluationIndicatorType;
        automaticCode: AutomaticIndicatorCode;
    }>({
        name: '',
        weight: 20,
        profileId: '',
        indicatorDescription: '',
        indicatorType: 'Manual',
        automaticCode: 'PPC'
    });

    // Diálogo Rango
    const [rangeDialogOpen, setRangeDialogOpen] = useState(false);
    const [editingRange, setEditingRange] = useState<EvaluationRange | null>(null);

    // Diálogo de Confirmación de Eliminación
    const [confirmDelete, setConfirmDelete] = useState<{
        type: 'area' | 'param' | 'range';
        areaId?: string;
        paramId?: string;
        rangeId?: string;
        title: string;
    } | null>(null);

    // Cerrar menú ⋮ al hacer clic afuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setActiveMenuKey(null);
            }
        };
        if (activeMenuKey) {
            document.addEventListener('pointerdown', handleClickOutside);
            return () => document.removeEventListener('pointerdown', handleClickOutside);
        }
    }, [activeMenuKey]);

    const toggleAreaExpand = (areaId: string) => {
        setExpandedAreaIds(prev => {
            const next = new Set(prev);
            if (next.has(areaId)) next.delete(areaId);
            else next.add(areaId);
            return next;
        });
    };

    // Cálculos de ponderación
    const totalAreaWeight = areas.reduce((sum, a) => sum + (Number(a.weight) || 0), 0);
    const isTotalWeightValid = Math.round(totalAreaWeight) === 100;

    // --- ACCIONES DE ÁREA ---
    const handleOpenAddArea = () => {
        setEditingArea({ name: '', weight: 25 });
        setAreaDialogOpen(true);
    };

    const handleOpenEditArea = (area: EvaluationArea) => {
        setEditingArea({ id: area.id, name: area.name, weight: Number(area.weight) || 0 });
        setAreaDialogOpen(true);
    };

    const handleSaveArea = () => {
        if (!editingArea.name.trim()) return;
        setAreas(prev => {
            if (editingArea.id) {
                return prev.map(a => a.id === editingArea.id ? { ...a, name: editingArea.name.trim(), weight: editingArea.weight } : a);
            } else {
                const nextCorrelative = prev.length + 1;
                const newArea: EvaluationArea = {
                    id: `area-${Date.now()}`,
                    correlative: nextCorrelative,
                    name: editingArea.name.trim(),
                    weight: editingArea.weight,
                    parameters: []
                };
                return [...prev, newArea];
            }
        });
        setAreaDialogOpen(false);
    };

    const handleDeleteAreaConfirm = () => {
        if (!confirmDelete?.areaId) return;
        setAreas(prev => {
            const filtered = prev.filter(a => a.id !== confirmDelete.areaId);
            return filtered.map((a, idx) => ({ ...a, correlative: idx + 1 }));
        });
        setConfirmDelete(null);
    };

    // --- ACCIONES DE PARÁMETRO ---
    const handleOpenAddParam = (areaId: string) => {
        setTargetAreaIdForParam(areaId);
        setEditingParam({
            name: '',
            weight: 20,
            profileId: globalProfiles.filter(p => !p.isDeleted)[0]?.id || '',
            indicatorDescription: '',
            indicatorType: 'Manual',
            automaticCode: 'PPC'
        });
        setParamDialogOpen(true);
    };

    const handleOpenEditParam = (areaId: string, param: EvaluationParameter) => {
        setTargetAreaIdForParam(areaId);
        setEditingParam({
            id: param.id,
            name: param.name,
            weight: Number(param.weight) || 0,
            profileId: param.profileId || '',
            indicatorDescription: param.indicatorDescription || '',
            indicatorType: param.indicatorType || 'Manual',
            automaticCode: param.automaticCode || 'PPC'
        });
        setParamDialogOpen(true);
    };

    const handleSaveParam = () => {
        if (!editingParam.name.trim() || !targetAreaIdForParam) return;
        const selectedProfile = globalProfiles.find(p => p.id === editingParam.profileId);

        setAreas(prev => prev.map(area => {
            if (area.id !== targetAreaIdForParam) return area;

            let updatedParams = [...area.parameters];
            if (editingParam.id) {
                updatedParams = updatedParams.map(p => {
                    if (p.id === editingParam.id) {
                        return {
                            ...p,
                            name: editingParam.name.trim(),
                            weight: editingParam.weight,
                            profileId: editingParam.profileId,
                            profileName: selectedProfile?.name || '',
                            indicatorDescription: editingParam.indicatorDescription.trim(),
                            indicatorType: editingParam.indicatorType,
                            automaticCode: editingParam.indicatorType === 'Automatico' ? editingParam.automaticCode : undefined
                        };
                    }
                    return p;
                });
            } else {
                const nextCorrelative = updatedParams.length + 1;
                const newParam: EvaluationParameter = {
                    id: `param-${Date.now()}`,
                    correlative: nextCorrelative,
                    name: editingParam.name.trim(),
                    weight: editingParam.weight,
                    profileId: editingParam.profileId,
                    profileName: selectedProfile?.name || '',
                    indicatorDescription: editingParam.indicatorDescription.trim(),
                    indicatorType: editingParam.indicatorType,
                    automaticCode: editingParam.indicatorType === 'Automatico' ? editingParam.automaticCode : undefined
                };
                updatedParams.push(newParam);
            }

            return {
                ...area,
                parameters: updatedParams.map((p, idx) => ({ ...p, correlative: idx + 1 }))
            };
        }));

        setParamDialogOpen(false);
    };

    const handleDeleteParamConfirm = () => {
        if (!confirmDelete?.areaId || !confirmDelete?.paramId) return;
        setAreas(prev => prev.map(area => {
            if (area.id !== confirmDelete.areaId) return area;
            const filtered = area.parameters.filter(p => p.id !== confirmDelete.paramId);
            return {
                ...area,
                parameters: filtered.map((p, idx) => ({ ...p, correlative: idx + 1 }))
            };
        }));
        setConfirmDelete(null);
    };

    // --- ACCIONES DE RANGO ---
    const handleOpenEditRange = (range: EvaluationRange) => {
        setEditingRange({ ...range });
        setRangeDialogOpen(true);
    };

    const handleSaveRange = () => {
        if (!editingRange) return;
        setRanges(prev => prev.map(r => r.id === editingRange.id ? editingRange : r));
        setRangeDialogOpen(false);
    };

    const handleMoveRange = (index: number, direction: 'up' | 'down') => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === ranges.length - 1) return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        setRanges(prev => {
            const next = [...prev];
            const temp = next[index];
            next[index] = next[targetIndex];
            next[targetIndex] = temp;
            return next;
        });
    };

    const handleDeleteRangeConfirm = () => {
        if (!confirmDelete?.rangeId) return;
        setRanges(prev => prev.filter(r => r.id !== confirmDelete.rangeId));
        setConfirmDelete(null);
    };

    // Guardar global
    const handleSaveGlobalConfig = () => {
        onSaveConfig({
            ...config,
            areas,
            ranges,
            updatedAt: new Date().toISOString()
        });
        onClose();
    };

    return (
        <KlFullModal
            open={open}
            onClose={onClose}
            title="Configuración de Evaluación de SC"
            subtitle="Áreas, parámetros ponderados y umbrales"
            footer={
                <div className="flex items-center gap-2 w-full">
                    <button
                        type="button"
                        onClick={onClose}
                        className="b b-o h-11 rounded-[10px] text-[13px] font-bold text-center"
                        style={{ width: '36%' }}
                    >
                        Cerrar
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveGlobalConfig}
                        className="b b-p flex-1 h-11 rounded-[10px] text-[13px] font-bold text-center"
                    >
                        Guardar configuración
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-3 p-3 pb-8">
                {/* Control Segmentado .kl-segmented de 36px y ancho completo */}
                <div className="kl-segmented w-full h-[36px] bg-[#E2E8F0] dark:bg-slate-800 rounded-[10px] p-[3px] flex shrink-0">
                    <button
                        type="button"
                        onClick={() => setActiveTab('evaluacion')}
                        className={`flex-1 rounded-[8px] text-[13px] font-semibold transition-all ${
                            activeTab === 'evaluacion'
                                ? 'is-on bg-white dark:bg-slate-700 text-[#1E3A8A] dark:text-blue-300 font-bold shadow-sm'
                                : 'text-[#64748B] dark:text-slate-400'
                        }`}
                    >
                        Evaluación
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('rangos')}
                        className={`flex-1 rounded-[8px] text-[13px] font-semibold transition-all ${
                            activeTab === 'rangos'
                                ? 'is-on bg-white dark:bg-slate-700 text-[#1E3A8A] dark:text-blue-300 font-bold shadow-sm'
                                : 'text-[#64748B] dark:text-slate-400'
                        }`}
                    >
                        Rangos
                    </button>
                </div>

                {/* Resumen fijo bajo el segmentado: Ponderación total + N áreas */}
                <div className="flex items-center justify-between py-1.5 px-0.5 border-b border-slate-100 dark:border-slate-800 shrink-0">
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] font-semibold text-[#0F172A] dark:text-slate-200">
                            Ponderación total
                        </span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-[10px] ${
                            isTotalWeightValid
                                ? 'bg-[#DCFCE7] text-[#166534] dark:bg-emerald-950/40 dark:text-emerald-300'
                                : 'bg-[#FEE2E2] text-[#991B1B] dark:bg-red-950/40 dark:text-red-300'
                        }`}>
                            {totalAreaWeight}%
                        </span>
                    </div>
                    <span className="text-[12px] font-medium text-[#64748B] dark:text-slate-400">
                        {areas.length} áreas
                    </span>
                </div>

                {/* CONTENIDO PESTAÑA: EVALUACIÓN */}
                {activeTab === 'evaluacion' && (
                    <div className="flex flex-col gap-3">
                        {/* Botón tonal superior para agregar área */}
                        {hasEditPermission && (
                            <button
                                type="button"
                                onClick={handleOpenAddArea}
                                className="b b-t w-full h-10 rounded-[10px] text-[13px] font-bold flex items-center justify-center gap-1.5 shrink-0"
                            >
                                <span className="material-symbols-outlined text-[18px]">add</span>
                                <span>Agregar Área de Evaluación</span>
                            </button>
                        )}

                        {areas.length === 0 ? (
                            <div className="card text-center py-8 text-xs text-slate-400 italic">
                                No se han configurado áreas de evaluación.
                            </div>
                        ) : (
                            areas.map((area, aIdx) => {
                                const isExpanded = expandedAreaIds.has(area.id);
                                const areaMenuKey = `area-${area.id}`;
                                const isAreaMenuOpen = activeMenuKey === areaMenuKey;

                                const sumParamWeights = area.parameters.reduce((sum, p) => sum + (Number(p.weight) || 0), 0);
                                const isParamSum100 = Math.round(sumParamWeights) === 100;

                                return (
                                    <div
                                        key={area.id}
                                        className="card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex flex-col gap-2.5 relative"
                                    >
                                        {/* Primera línea: Número, nombre 14px/700, ⋮ y chevron */}
                                        <div className="flex items-center justify-between gap-2">
                                            <div
                                                onClick={() => toggleAreaExpand(area.id)}
                                                className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer select-none"
                                            >
                                                <div className="w-6 h-6 rounded-[8px] bg-[#EFF6FF] dark:bg-blue-950/50 text-[#2563EB] dark:text-blue-300 font-mono font-bold text-[11px] flex items-center justify-center shrink-0">
                                                    {aIdx + 1}
                                                </div>
                                                <h4 className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100 leading-snug line-clamp-2">
                                                    {area.name}
                                                </h4>
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0">
                                                {/* Menú ⋮ del área */}
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveMenuKey(isAreaMenuOpen ? null : areaMenuKey)}
                                                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                                    </button>

                                                    {isAreaMenuOpen && (
                                                        <div
                                                            ref={menuRef}
                                                            className="absolute right-0 top-9 z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 min-w-[170px]"
                                                        >
                                                            {hasEditPermission && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setActiveMenuKey(null);
                                                                            handleOpenAddParam(area.id);
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px] text-blue-600">add</span>
                                                                        <span>Añadir parámetro</span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setActiveMenuKey(null);
                                                                            handleOpenEditArea(area);
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px] text-slate-500">edit</span>
                                                                        <span>Editar</span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setActiveMenuKey(null);
                                                                            setConfirmDelete({
                                                                                type: 'area',
                                                                                areaId: area.id,
                                                                                title: area.name
                                                                            });
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                                    >
                                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                                        <span>Eliminar</span>
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Chevron */}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleAreaExpand(area.id)}
                                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">
                                                        {isExpanded ? 'expand_less' : 'expand_more'}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Segunda línea: "Ponderador 25%" e insignia "Suma parámetros 100%" */}
                                        <div className="flex items-center gap-2 pt-0.5">
                                            <span className="text-[12px] font-semibold text-[#475569] dark:text-slate-300">
                                                Ponderador {area.weight}%
                                            </span>
                                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-[10px] ${
                                                isParamSum100
                                                    ? 'bg-[#DCFCE7] text-[#166534] dark:bg-emerald-950/40 dark:text-emerald-300'
                                                    : 'bg-[#FEE2E2] text-[#991B1B] dark:bg-red-950/40 dark:text-red-300'
                                            }`}>
                                                Suma parámetros {sumParamWeights}%
                                            </span>
                                        </div>

                                        {/* Parámetros: filas de 44px (desplegadas en acordeón) */}
                                        {isExpanded && (
                                            <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 mt-1">
                                                {area.parameters.length === 0 ? (
                                                    <div className="text-center py-3 text-xs text-slate-400 italic">
                                                        Sin parámetros. Toca en ⋮ para añadir uno.
                                                    </div>
                                                ) : (
                                                    area.parameters.map((param, pIdx) => {
                                                        const paramMenuKey = `param-${param.id}`;
                                                        const isParamMenuOpen = activeMenuKey === paramMenuKey;
                                                        const assignedProfile = globalProfiles.find(p => p.id === param.profileId);
                                                        const profileLabel = assignedProfile?.name || param.profileName || 'Sin asignar';

                                                        return (
                                                            <div
                                                                key={param.id}
                                                                className="min-h-[44px] py-1.5 px-2 rounded-[10px] bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2"
                                                            >
                                                                <div className="flex flex-col flex-1 min-w-0 pr-1">
                                                                    {/* Primera línea: código monoespaciado, nombre hasta 2 líneas, ponderador a la derecha */}
                                                                    <div className="flex items-baseline justify-between gap-2">
                                                                        <div className="flex items-baseline gap-1.5 flex-1 min-w-0">
                                                                            <span className="font-mono text-[11px] font-bold text-[#64748B] dark:text-slate-400 shrink-0">
                                                                                {area.correlative}.{param.correlative}
                                                                            </span>
                                                                            <span className="text-[13px] font-medium text-[#0F172A] dark:text-slate-200 leading-snug line-clamp-2">
                                                                                {param.name}
                                                                            </span>
                                                                        </div>
                                                                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-[10px] bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-950/50 dark:text-blue-300 shrink-0">
                                                                            {param.weight}%
                                                                        </span>
                                                                    </div>

                                                                    {/* Segunda línea: responsable o cargo a 11px gris */}
                                                                    <div className="flex items-center gap-2 mt-0.5">
                                                                        <span className="text-[11px] text-[#64748B] dark:text-slate-400 truncate">
                                                                            {profileLabel}
                                                                        </span>
                                                                        {param.indicatorType === 'Automatico' && (
                                                                            <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 shrink-0">
                                                                                Auto: {param.automaticCode}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Menú ⋮ del parámetro */}
                                                                {hasEditPermission && (
                                                                    <div className="relative shrink-0">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setActiveMenuKey(isParamMenuOpen ? null : paramMenuKey)}
                                                                            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[18px]">more_vert</span>
                                                                        </button>

                                                                        {isParamMenuOpen && (
                                                                            <div
                                                                                ref={menuRef}
                                                                                className="absolute right-0 top-8 z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 min-w-[140px]"
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setActiveMenuKey(null);
                                                                                        handleOpenEditParam(area.id, param);
                                                                                    }}
                                                                                    className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px] text-slate-500">edit</span>
                                                                                    <span>Editar</span>
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setActiveMenuKey(null);
                                                                                        setConfirmDelete({
                                                                                            type: 'param',
                                                                                            areaId: area.id,
                                                                                            paramId: param.id,
                                                                                            title: param.name
                                                                                        });
                                                                                    }}
                                                                                    className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                                                >
                                                                                    <span className="material-symbols-outlined text-[16px]">delete</span>
                                                                                    <span>Eliminar</span>
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* CONTENIDO PESTAÑA: RANGOS */}
                {activeTab === 'rangos' && (
                    <div className="flex flex-col gap-2.5">
                        {ranges.map((range, index) => {
                            const rangeMenuKey = `range-${range.id}`;
                            const isRangeMenuOpen = activeMenuKey === rangeMenuKey;

                            // Formato de operador y valores
                            let conditionLabel = '';
                            if (range.operator === 'between') {
                                conditionLabel = `${range.minValue ?? 0}% a ${range.maxValue ?? 100}%`;
                            } else {
                                conditionLabel = `${range.operator} ${range.value}%`;
                            }

                            return (
                                <div
                                    key={range.id}
                                    className="card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 relative py-2.5"
                                >
                                    {/* Orden + Icono de distinción + Nombre + Condición */}
                                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                        <span className="w-5 h-5 rounded-[6px] bg-slate-100 dark:bg-slate-800 text-[#64748B] dark:text-slate-400 font-bold text-[10px] flex items-center justify-center shrink-0">
                                            #{index + 1}
                                        </span>

                                        {/* Icono de Nivel / Distinción */}
                                        <div
                                            className="w-7 h-7 rounded-[8px] flex items-center justify-center shrink-0"
                                            style={{ backgroundColor: `${range.color}20`, color: range.color }}
                                        >
                                            <span className="material-symbols-outlined text-[18px]">
                                                {range.icon || 'sentiment_satisfied'}
                                            </span>
                                        </div>

                                        {/* Nombre y Valor del rango */}
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 leading-snug truncate">
                                                {range.name}
                                            </span>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <span className="font-mono text-[11px] font-bold text-[#475569] dark:text-slate-300">
                                                    {conditionLabel}
                                                </span>
                                                <span
                                                    className="text-[10px] font-bold px-1.5 py-0.2 rounded"
                                                    style={{ backgroundColor: `${range.color}25`, color: range.color }}
                                                >
                                                    {range.level}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Menú ⋮ del rango */}
                                    {hasEditPermission && (
                                        <div className="relative shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setActiveMenuKey(isRangeMenuOpen ? null : rangeMenuKey)}
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                            </button>

                                            {isRangeMenuOpen && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute right-0 top-9 z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 min-w-[150px]"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveMenuKey(null);
                                                            handleOpenEditRange(range);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px] text-slate-500">edit</span>
                                                        <span>Editar rango</span>
                                                    </button>
                                                    {index > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setActiveMenuKey(null);
                                                                handleMoveRange(index, 'up');
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] text-slate-500">arrow_upward</span>
                                                            <span>Subir prioridad</span>
                                                        </button>
                                                    )}
                                                    {index < ranges.length - 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setActiveMenuKey(null);
                                                                handleMoveRange(index, 'down');
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] text-slate-500">arrow_downward</span>
                                                            <span>Bajar prioridad</span>
                                                        </button>
                                                    )}
                                                    {ranges.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setActiveMenuKey(null);
                                                                setConfirmDelete({
                                                                    type: 'range',
                                                                    rangeId: range.id,
                                                                    title: range.name
                                                                });
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px]">delete</span>
                                                            <span>Eliminar</span>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* DIÁLOGO: EDITAR / CREAR ÁREA */}
            <KlDialog
                open={areaDialogOpen}
                onClose={() => setAreaDialogOpen(false)}
                title={editingArea.id ? 'Editar Área' : 'Nueva Área'}
                confirmText="Guardar"
                cancelText="Cancelar"
                onConfirm={handleSaveArea}
            >
                <div className="flex flex-col gap-3 py-2">
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Nombre del Área
                        </label>
                        <input
                            type="text"
                            value={editingArea.name}
                            onChange={(e) => setEditingArea(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Ej: Calidad y Autocontrol"
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Ponderador (%)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={editingArea.weight}
                            onChange={(e) => setEditingArea(prev => ({ ...prev, weight: parseFloat(e.target.value) || 0 }))}
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] font-mono text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                        />
                    </div>
                </div>
            </KlDialog>

            {/* DIÁLOGO: EDITAR / CREAR PARÁMETRO */}
            <KlDialog
                open={paramDialogOpen}
                onClose={() => setParamDialogOpen(false)}
                title={editingParam.id ? 'Editar Parámetro' : 'Nuevo Parámetro'}
                confirmText="Guardar"
                cancelText="Cancelar"
                onConfirm={handleSaveParam}
            >
                <div className="flex flex-col gap-2.5 py-2">
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Nombre del Parámetro
                        </label>
                        <textarea
                            rows={2}
                            value={editingParam.name}
                            onChange={(e) => setEditingParam(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Ej: Protocolos de liberación conforme"
                            className="w-full p-2.5 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB] resize-none"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Ponderador (%)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={editingParam.weight}
                            onChange={(e) => setEditingParam(prev => ({ ...prev, weight: parseFloat(e.target.value) || 0 }))}
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] font-mono text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                        />
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Responsable / Cargo
                        </label>
                        <select
                            value={editingParam.profileId}
                            onChange={(e) => setEditingParam(prev => ({ ...prev, profileId: e.target.value }))}
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                        >
                            <option value="">Sin asignar</option>
                            {globalProfiles.filter(p => !p.isDeleted).map(profile => (
                                <option key={profile.id} value={profile.id}>
                                    {profile.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                            Tipo de Indicador
                        </label>
                        <select
                            value={editingParam.indicatorType}
                            onChange={(e) => setEditingParam(prev => ({ ...prev, indicatorType: e.target.value as EvaluationIndicatorType }))}
                            className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                        >
                            <option value="Manual">Manual</option>
                            <option value="Automatico">Automático (del sistema)</option>
                        </select>
                    </div>
                    {editingParam.indicatorType === 'Automatico' && (
                        <div>
                            <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                Indicador Automático
                            </label>
                            <select
                                value={editingParam.automaticCode}
                                onChange={(e) => setEditingParam(prev => ({ ...prev, automaticCode: e.target.value as AutomaticIndicatorCode }))}
                                className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            >
                                {AUTOMATIC_INDICATOR_OPTIONS.map(opt => (
                                    <option key={opt.code} value={opt.code}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </KlDialog>

            {/* DIÁLOGO: EDITAR RANGO */}
            <KlDialog
                open={rangeDialogOpen}
                onClose={() => setRangeDialogOpen(false)}
                title="Editar Rango"
                confirmText="Guardar"
                cancelText="Cancelar"
                onConfirm={handleSaveRange}
            >
                {editingRange && (
                    <div className="flex flex-col gap-2.5 py-2">
                        <div>
                            <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                Nombre del Rango
                            </label>
                            <input
                                type="text"
                                value={editingRange.name}
                                onChange={(e) => setEditingRange(prev => prev ? { ...prev, name: e.target.value } : null)}
                                className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            />
                        </div>

                        <div>
                            <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                Condición / Operador
                            </label>
                            <select
                                value={editingRange.operator}
                                onChange={(e) => setEditingRange(prev => prev ? { ...prev, operator: e.target.value as EvaluationRangeOperator } : null)}
                                className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            >
                                {RANGE_OPERATOR_OPTIONS.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                ))}
                            </select>
                        </div>

                        {editingRange.operator === 'between' ? (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[11px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                        Mínimo (%)
                                    </label>
                                    <input
                                        type="number"
                                        value={editingRange.minValue ?? 0}
                                        onChange={(e) => setEditingRange(prev => prev ? { ...prev, minValue: parseFloat(e.target.value) || 0 } : null)}
                                        className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] font-mono outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                        Máximo (%)
                                    </label>
                                    <input
                                        type="number"
                                        value={editingRange.maxValue ?? 100}
                                        onChange={(e) => setEditingRange(prev => prev ? { ...prev, maxValue: parseFloat(e.target.value) || 0 } : null)}
                                        className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] font-mono outline-none"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                    Valor (%)
                                </label>
                                <input
                                    type="number"
                                    value={editingRange.value ?? 80}
                                    onChange={(e) => setEditingRange(prev => prev ? { ...prev, value: parseFloat(e.target.value) || 0 } : null)}
                                    className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] font-mono outline-none"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-[12px] font-semibold text-[#475569] dark:text-slate-300 mb-1 block">
                                Nivel de Desempeño
                            </label>
                            <select
                                value={editingRange.level}
                                onChange={(e) => {
                                    const nextLevel = e.target.value as VisualDistinctionLevel;
                                    const dInfo = DISTINCTION_LEVELS.find(d => d.level === nextLevel) || DISTINCTION_LEVELS[0];
                                    setEditingRange(prev => prev ? {
                                        ...prev,
                                        level: nextLevel,
                                        color: dInfo.color,
                                        icon: dInfo.icons[prev.visualType || 'carita']
                                    } : null);
                                }}
                                className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[10px] text-[13px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            >
                                {DISTINCTION_LEVELS.map(d => (
                                    <option key={d.level} value={d.level}>{d.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </KlDialog>

            {/* DIÁLOGO: CONFIRMACIÓN ELIMINAR */}
            <KlDialog
                open={confirmDelete !== null}
                onClose={() => setConfirmDelete(null)}
                title={
                    confirmDelete?.type === 'area'
                        ? '¿Eliminar área?'
                        : confirmDelete?.type === 'param'
                        ? '¿Eliminar parámetro?'
                        : '¿Eliminar rango?'
                }
                message={`¿Confirmas que deseas eliminar "${confirmDelete?.title}"? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
                cancelText="Cancelar"
                danger={true}
                onConfirm={() => {
                    if (confirmDelete?.type === 'area') handleDeleteAreaConfirm();
                    else if (confirmDelete?.type === 'param') handleDeleteParamConfirm();
                    else handleDeleteRangeConfirm();
                }}
            />
        </KlFullModal>
    );
};
